import Utils from './Utils';
import EventEmitter from './EventEmitter';

class ContextMenu extends EventEmitter {
    constructor(domElement, triggerDomElement = null, options = {}) {
        super();

        this.options = Object.assign({
            positionToTriggerCenter: true,
            enableSearch: false,
            dataSource: null,
            searchUrl: null,
            searchUrlQueryFieldName: 'q',
            searchUrlAdditionalFields: {},
        }, options);

        if (!ContextMenu.contextMenuMap) {
            ContextMenu.contextMenuMap = new Map();
        }

        ContextMenu.contextMenuMap.set(domElement, this);

        this.domElement = domElement;
        this.triggerDomElement = triggerDomElement || null;
        this.menuItemWrapperDomElement = this.domElement.getElementsByClassName('menu-item-wrapper')[0];
        this.menuItemDomElements = this.menuItemWrapperDomElement.getElementsByClassName('menu-item');

        this.searchBarWrapperDomElement = null;
        this.searchBarDomElement = null;

        this.onTriggerClickEvent = null;
        this.documentMouseDownEvent = null;
        this.documentClickEvent = null;

        if (options.enableSearch === true) {
            this.createSearchBar();
        }

        if (!!options.dataSource) {
            this.loadDataSource();
        }

        this.bindEvents();
    }

    destruct() {
        this.unbindEvents();
        ContextMenu.contextMenuMap.delete(this.domElement);
    }

    bindEvents() {
        if (!!this.triggerDomElement) {
            this.onTriggerClickEvent = this.onTriggerClick.bind(this);
            this.triggerDomElement.addEventListener('click', this.onTriggerClickEvent);
        }

        for (let i = 0; i < this.menuItemDomElements.length; i++) {
            this.menuItemDomElements[i].addEventListener('click', this.onMenuItemClick.bind(this));
        }
    }

    unbindEvents() {
        if (!!this.triggerDomElement) {
            if (!!this.onTriggerClickEvent) {
                this.triggerDomElement.removeEventListener('click', this.onTriggerClickEvent);
            }
        }
    }

    createSearchBar() {
        this.searchBarWrapperDomElement = document.createElement('div');
        this.searchBarWrapperDomElement.className = 'context-menu-search-wrapper';

        if (!!this.options.searchUrl) {
            this.searchBarResultsWrapperDomElement = document.createElement('div');
            this.searchBarResultsWrapperDomElement.className = 'context-menu-search-results-wrapper';
        }

        this.searchBarDomElement = document.createElement('input');
        this.searchBarDomElement.className = 'textbox context-menu-search-input';
        this.searchBarDomElement.placeholder = 'Search for records...';

        if (!!this.options.searchUrl) {
            this.searchBarDomElement.addEventListener('keyup', this.onSearchInputKeyUpForSearch.bind(this));
        } else {
            this.searchBarDomElement.addEventListener('keyup', this.onSearchInputKeyUpForFilter.bind(this));
        }

        this.searchBarWrapperDomElement.appendChild(this.searchBarDomElement);
        if (!!this.options.searchUrl) {
            this.domElement.prepend(this.searchBarResultsWrapperDomElement);
        }

        this.domElement.prepend(this.searchBarWrapperDomElement);
    }

    onSearchInputKeyUpForFilter() {
        for (let i = 0; i < this.menuItemDomElements.length; i++) {
            const menuItemDomElement = this.menuItemDomElements[i];
            if (this.searchBarDomElement.value.length === 0) {
                menuItemDomElement.style.display = 'block';
                continue;
            }

            const lowercaseSearchValue = (this.searchBarDomElement.value || '').toLowerCase();
            const lowercaseValue = (menuItemDomElement.dataset.value || '').toLowerCase();
            const lowercaseTextContent = (menuItemDomElement.textContent || '').toLowerCase();

            const containsText =
                lowercaseValue.indexOf(lowercaseSearchValue) !== -1 ||
                lowercaseTextContent.indexOf(lowercaseSearchValue) !== -1;

            menuItemDomElement.style.display = containsText ? 'block' : 'none';
        }
    }

    onSearchInputKeyUpForSearch() {
        if (this.searchTimeout !== null) {
            clearTimeout(this.searchTimeout);
        }

        this.searchBarResultsWrapperDomElement.innerHTML = '<p id="topbar-search-menu-empty">' +
            '<img src="/images/content_loader_black.svg" alt="" class="loader-image loader-image-black">' +
            '</p>';

        this.searchTimeout = window.setTimeout(this.performSearch.bind(this), 600);
    }

    loadDataSource() {
        Utils.httpRequest('GET', this.options.dataSource, {}, (err, data) => {
            this.clearItems();

            const modelKeys = Object.keys(data);

            for (let i = 0; i < modelKeys.length; i++) {
                const modelData = data[modelKeys[i]];

                for (let j = 0; j < modelData['results'].length; j++) {
                    const row = modelData['results'][j];
                    this.addItem(row['$title'] || row['_id'].toString(), row['_id'].toString());
                }
            }

            this.emit({
                type: 'loaded',
            });
        });
    }

    performSearch() {
        if (this.searchTimeout !== null) {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = null;
        }

        const query = this.searchBarDomElement.value.trim();
        this.createSearchRequest(query, this.onSearchCompleted.bind(this));
    }

    createSearchRequest(query, cb) {
        const searchQueryObj = {};
        searchQueryObj[this.options.searchUrlQueryFieldName] = query;

        const requestQuery = Object.assign({}, this.options.searchUrlAdditionalFields, searchQueryObj);

        Utils.httpRequest('GET', this.options.searchUrl, requestQuery, cb);
    }

    onSearchCompleted(err, data) {
        this.searchBarResultsWrapperDomElement.innerHTML = '';
        this.clearItems();

        const modelKeys = Object.keys(data);

        let hasSearchResults = false;
        for (let i = 0; i < modelKeys.length; i++) {
            const modelData = data[modelKeys[i]];

            if (modelData['results'].length > 0) {
                hasSearchResults = true;
            }

            for (let j = 0; j < modelData['results'].length; j++) {
                const row = modelData['results'][j];
                this.addItem(row['$title'] || row['_id'].toString(), row['_id'].toString());
            }
        }

        if (hasSearchResults === false) {
            this.searchBarResultsWrapperDomElement.innerHTML = '<p id="topbar-search-menu-empty">No Search Results...</p>';
        }
    }

    addItem(label, id = null) {
        const optionAnchorElement = document.createElement('a');
        optionAnchorElement.href = 'javascript://';
        optionAnchorElement.className = 'menu-item';
        optionAnchorElement.dataset.value = id || label;
        optionAnchorElement.addEventListener('click', this.onMenuItemClick.bind(this));

        optionAnchorElement.appendChild(document.createTextNode(label));
        this.menuItemWrapperDomElement.appendChild(optionAnchorElement);

        if (label === null && id !== null) {
            if (!!this.options.searchUrl) {
                this.createSearchRequest(id, function (err, data) {
                    const modelKeys = Object.keys(data);

                    for (let i = 0; i < modelKeys.length; i++) {
                        const modelData = data[modelKeys[i]];

                        for (let j = 0; j < modelData['results'].length; j++) {
                            const row = modelData['results'][j];
                            const resultId = row['_id'].toString();

                            if (resultId === id) {
                                this.domElement.innerHTML = '';
                                this.domElement.appendChild(document.createTextNode(row['$title'] || resultId));
                                break;
                            }
                        }
                    }
                }.bind({domElement: optionAnchorElement}));
            }
        }
    }

    clearItems() {
        while (this.menuItemDomElements.length > 0) {
            this.menuItemDomElements[0].parentNode.removeChild(this.menuItemDomElements[0]);
        }
    }

    onMenuItemClick(e) {
        const menuItem = e.target.closest('.menu-item');
        if (!menuItem) {
            return;
        }

        if (this.hasListeners('item_click') === false) {
            return;
        }

        e.preventDefault();
        this.emit({
            type: 'item_click',
            value: menuItem.dataset.value,
            text: menuItem.textContent,
        });
    }

    getRectWithTransformedParent(el) {
        const rect = el.getBoundingClientRect();
        let transformedElement = el.parentNode;
        let correctionX = 0;
        let correctionY = 0;

        do {
            const style = window.getComputedStyle(transformedElement);
            if (style.transform !== 'none') {
                const transformedRect = transformedElement.getBoundingClientRect();
                correctionX = transformedRect.x;
                correctionY = transformedRect.y;
            }

            transformedElement = transformedElement.parentNode;
            if (!transformedElement) {
                break;
            }
        } while (transformedElement.isSameNode(document.body) === false);

        return new DOMRect(rect.x - correctionX, rect.y - correctionY, rect.width, rect.height);
    }

    convertToFixed() {
        this.domElement.style.position = 'absolute';
        this.domElement.style.left = 'unset';
        this.domElement.style.top = 'unset';

        const rect = this.getRectWithTransformedParent(this.domElement);

        this.domElement.style.position = 'fixed';
        this.domElement.style.left = rect.left + 'px';
        this.domElement.style.top = rect.top + 'px';
        this.domElement.style.width = rect.width + 'px';
    }

    show() {
        this.domElement.classList.toggle('visible', true);
        this.convertToFixed();
    }

    hide() {
        this.domElement.classList.toggle('visible', false);
    }

    setPosition(x, y) {
        this.domElement.style.position = 'fixed';
        this.domElement.style.left = x + 'px';
        this.domElement.style.top = y + 'px';
    }

    onTriggerClick(e) {
        e.preventDefault();

        this.show();
        this.correctMenuPosition(e.clientX, e.clientY);

        this.documentMouseDownEvent = this.onDocumentMouseDown.bind(this);
        this.documentClickEvent = this.onDocumentClick.bind(this);

        document.addEventListener('mousedown', this.documentMouseDownEvent);
        document.addEventListener('click', this.documentClickEvent);
    }

    onDocumentMouseDown(e) {
        if (this.domElement.contains(e.target)) {
            return;
        }

        document.removeEventListener('mousedown', this.documentMouseDownEvent);
        this.hide();
    }

    onDocumentClick(e) {
        if (this.triggerDomElement.contains(e.target) ||
            e.target.tagName === 'INPUT' ||
            e.target.tagName === 'TEXTAREA') {
            return;
        }

        document.removeEventListener('click', this.documentClickEvent);
        this.hide();
    }

    correctMenuPosition(mouseX, mouseY, width = null) {
        if (!!width) {
            this.domElement.style.width = width + 'px';
        }

        if (!!mouseX || !!mouseY) {
            let positionX = mouseX || 0;
            let positionY = mouseY || 0;

            const triggerRect = this.getRectWithTransformedParent(this.triggerDomElement);
            const menuRect = this.domElement.getBoundingClientRect();

            if (!!this.triggerDomElement) {
                positionX = triggerRect.left + (this.options['positionToTriggerCenter'] ? (triggerRect.width / 2) : 0);
                positionY = triggerRect.bottom - (this.options['positionToTriggerCenter'] ? 15 : 0);
            }

            if ((positionX + menuRect.width) >= window.innerWidth) {
                positionX -= menuRect.width;
            }

            if ((positionY + menuRect.height) >= window.innerHeight) {
                positionY -= menuRect.height + (this.options['positionToTriggerCenter'] ? 0 : triggerRect.height);
            }

            this.setPosition(positionX, positionY);
        }
    }

    static getContextMenu(element) {
        if (!ContextMenu.contextMenuMap) {
            return null;
        }

        return ContextMenu.contextMenuMap.get(element);
    }
}

export default ContextMenu;
